/**
 * SubtaskItem - Ítem individual de subtarea
 */

import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, Info, Clock, AlertTriangle } from 'lucide-react';
import { useProtocolStore } from '@/store';
import { cn, formatMinutes } from '@/core/utils';
import { getLayerColors } from '@/core/constants';
import { feedbackSuccess } from '@/services/feedback';
import type { Subtask, Layer } from '@/core/types';

interface SubtaskItemProps {
  subtask: Subtask;
  protocolId: string;
  layer: Layer;
  index: number;
}

export function SubtaskItem({
  subtask,
  protocolId,
  layer,
  index,
}: SubtaskItemProps) {
  const [justCompleted, setJustCompleted] = useState(false);
  const toggleSubtask = useProtocolStore((state) => state.toggleSubtask);
  const colors = getLayerColors(layer);

  const handleToggle = useCallback(() => {
    const wasCompleted = subtask.isDone;
    toggleSubtask(protocolId, subtask.id);

    if (!wasCompleted) {
      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 600);

      // Feedback haptico y sonoro (usa preferencias internamente)
      feedbackSuccess();
    }
  }, [protocolId, subtask.id, subtask.isDone, toggleSubtask]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg',
        'transition-colors duration-200 cursor-pointer',
        'hover:bg-slate-800/50',
        subtask.isDone && 'opacity-50',
        // Resaltar visualmente las tareas críticas no completadas
        subtask.isCritical && !subtask.isDone && 'bg-rose-950/20 border-l-2 border-rose-700'
      )}
      onClick={handleToggle}
      role="checkbox"
      aria-checked={subtask.isDone}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      {/* Checkbox */}
      <motion.div
        className={cn(
          'flex-shrink-0 w-5 h-5 rounded-md border-2',
          'flex items-center justify-center',
          'transition-all duration-200',
          subtask.isDone
            ? `${colors.bg} border-transparent`
            : 'border-slate-600 hover:border-slate-500'
        )}
        animate={justCompleted ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        {subtask.isDone && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </motion.div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm transition-colors',
              subtask.isDone
                ? 'line-through text-slate-500'
                : 'text-slate-200'
            )}
          >
            {subtask.label}
          </span>

          {subtask.isCritical && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-2xs font-semibold bg-rose-900/40 text-rose-300 rounded border border-rose-700/50">
              <AlertTriangle className="w-2.5 h-2.5" />
              Esencial
            </span>
          )}
        </div>

        {/* Nota científica */}
        {subtask.scientificNote && (
          <div className="flex items-start gap-1 mt-1">
            <Info className="w-3 h-3 text-slate-600 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-slate-500 italic">
              {subtask.scientificNote}
            </span>
          </div>
        )}
      </div>

      {/* Tiempo estimado */}
      {subtask.estimatedMinutes && (
        <div className="flex items-center gap-1 text-slate-600">
          <Clock className="w-3 h-3" />
          <span className="text-xs">{formatMinutes(subtask.estimatedMinutes)}</span>
        </div>
      )}
    </motion.div>
  );
}
